import { ArrowRight, TrendingUp, Flame, Target } from 'lucide-react';
import { useTrendsForYou } from '@/hooks/useTrends';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/components/ui/utils';
import { opportunityFor, type Trend } from '@/utils/trend';
import { getPathForPage } from '@/routes';

/**
 * Compact "Trending For You" card for the Omni home dashboard.
 * Shows the top-3 team-relevant trends with a one-click jump to the Trend Radar.
 */
export interface TrendsForYouWidgetProps {
  navigate?: (path: string) => void;
  className?: string;
}

export function TrendsForYouWidget({ navigate, className }: TrendsForYouWidgetProps) {
  const { data, isLoading, metadata } = useTrendsForYou(3);

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-3xl border border-gray-100 bg-white p-5 shadow-sm',
        className
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#f3ecff]">
            <TrendingUp className="h-3.5 w-3.5 text-[#be43ff]" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-[#101828]">Trending for you</span>
              {metadata.source === 'live' ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-700">
                  <span className="inline-block h-1 w-1 animate-pulse rounded-full bg-emerald-500" />
                  Live
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-gray-500">
                  Sample
                </span>
              )}
            </div>
            <div className="text-[11px] text-gray-500">
              Live signals matched to your brand this week
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate?.(getPathForPage('trends'))}
          className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#be43ff] hover:underline"
        >
          View all
          <ArrowRight className="h-3 w-3" />
        </button>
      </div>

      <ul className="flex flex-col gap-2">
        {isLoading && !data?.length
          ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-[60px] rounded-xl" />)
          : (data ?? []).map((t) => <Row key={t.id} trend={t} navigate={navigate} />)}
      </ul>
    </div>
  );
}

function Row({ trend, navigate }: { trend: Trend; navigate?: (path: string) => void }) {
  const opp = opportunityFor(trend);
  const oppStyles =
    opp === 'high'
      ? 'bg-[#d9f99d] text-[#1c3a00]'
      : opp === 'medium'
        ? 'bg-orange-100 text-orange-700'
        : 'bg-gray-100 text-gray-600';
  return (
    <button
      type="button"
      onClick={() => navigate?.(getPathForPage('trends'))}
      className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-3 text-left transition-all hover:border-[#be43ff]/40 hover:shadow-sm"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[13px] font-semibold text-[#101828]">{trend.title}</span>
          <Badge className={cn('border-0 text-[10px]', oppStyles)}>{opp.toUpperCase()}</Badge>
        </div>
        <div className="mt-1 flex items-center gap-3 text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1">
            <Flame className="h-3 w-3 text-orange-500" />
            {trend.virality_score}
          </span>
          <span className="inline-flex items-center gap-1">
            <Target className="h-3 w-3 text-[#be43ff]" />
            {trend.relevance.relevance_score}
          </span>
          <span className="truncate italic">{trend.relevance.suggested_action}</span>
        </div>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-gray-400" />
    </button>
  );
}
